import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  IconButton,
  InputAdornment,
  Divider,
} from "@mui/material";

import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Link as LinkIcon,
} from "@mui/icons-material";

import useAdminContext from "../../store/contexts/AdminContext";
import {
  AD_FORMATS,
  AD_STATUS,
  USER_ACCOUNT_TYPES,
  AD_CONFIG,
} from "../../constants/types";

const EditAdModal = ({ open, onClose, ad, onSuccess }) => {
  const { updateAdApiCall, authState } = useAdminContext();
  const { loading } = authState;

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    ad_type: "",
    imageUrl: "",
    targetUrl: "",
    visiblePlans: [],
    impressionGoal: "",
    priority: AD_CONFIG.DEFAULT_PRIORITY,
    status: "",
    startDate: "",
    endDate: "",
  });

  const [errors, setErrors] = useState({});

  // Format number with commas
  const formatNumber = (value) => {
    if (!value) return "";
    const numeric = value.replace(/\D/g, "");
    return numeric ? Number(numeric).toLocaleString() : "";
  };

  const unformatNumber = (value) => value.replace(/,/g, "");

  // Load selected ad into form
  useEffect(() => {
    if (ad && open) {
      setFormData({
        title: ad.title || "",
        description: ad.description || "",
        ad_type: ad.ad_type || AD_FORMATS.IMAGE.value,
        imageUrl: ad.metadata?.image_url || "",
        targetUrl: ad.click_url || "",
        visiblePlans: ad.visible_to_plans || [],
        impressionGoal: ad.impressions_goal
          ? Number(ad.impressions_goal).toLocaleString()
          : "",
        priority: ad.priority || AD_CONFIG.DEFAULT_PRIORITY,
        status: ad.status || AD_STATUS.DRAFT.value,
        startDate: ad.start_date
          ? new Date(ad.start_date).toISOString().substring(0, 10)
          : "",
        endDate: ad.end_date
          ? new Date(ad.end_date).toISOString().substring(0, 10)
          : "",
      });

      setErrors({});
    }
  }, [ad, open]);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!formData.description.trim())
      newErrors.description = "Description is required";

    if (!formData.imageUrl.trim()) newErrors.imageUrl = "Image URL is required";
    if (!formData.targetUrl.trim())
      newErrors.targetUrl = "Target URL is required";

    if (formData.endDate && formData.endDate < formData.startDate)
      newErrors.endDate = "End date cannot be before start date";

    if (formData.impressionGoal) {
      const val = Number(unformatNumber(formData.impressionGoal));
      if (isNaN(val) || val < 1)
        newErrors.impressionGoal = "Impression goal must be a positive number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    if (!ad?.id) return console.warn("Edit error: missing ad.id");

    const payload = {
      title: formData.title,
      description: formData.description,
      ad_type: formData.ad_type,
      priority: Number(formData.priority),
      start_date: formData.startDate ? new Date(formData.startDate) : null,
      end_date: formData.endDate ? new Date(formData.endDate) : null,
      visible_to_plans: formData.visiblePlans,
      click_url: formData.targetUrl,
      impressions_goal: formData.impressionGoal
        ? Number(unformatNumber(formData.impressionGoal))
        : null,
      metadata: {
        image_url: formData.imageUrl,
      },
      status: formData.status,
    };

    const result = await updateAdApiCall(ad.id, payload);
    if (result) {
      handleClose();
      onSuccess?.();
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  if (!ad) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h5">Edit Advertisement</Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ mt: 2 }}>
        <Grid container spacing={3}>
          {/* Title */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={handleChange("title")}
              error={!!errors.title}
              helperText={errors.title}
            />
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Description"
              value={formData.description}
              onChange={handleChange("description")}
              error={!!errors.description}
              helperText={errors.description}
            />
          </Grid>

          {/* Image URL */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Image URL"
              value={formData.imageUrl}
              onChange={handleChange("imageUrl")}
              error={!!errors.imageUrl}
              helperText={errors.imageUrl}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <UploadIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Target URL */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Target URL"
              value={formData.targetUrl}
              onChange={handleChange("targetUrl")}
              error={!!errors.targetUrl}
              helperText={errors.targetUrl}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Impression Goal with commas */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Impression Goal"
              value={formData.impressionGoal}
              onChange={(e) => {
                const formatted = formatNumber(e.target.value);
                setFormData((prev) => ({
                  ...prev,
                  impressionGoal: formatted,
                }));

                if (errors.impressionGoal) {
                  setErrors((prev) => ({ ...prev, impressionGoal: "" }));
                }
              }}
              error={!!errors.impressionGoal}
              helperText={errors.impressionGoal}
            />
          </Grid>

          {/* Ad Type */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Ad Type</InputLabel>
              <Select
                value={formData.ad_type}
                onChange={handleChange("ad_type")}
                label="Ad Type"
              >
                {Object.values(AD_FORMATS).map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Priority */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Priority (1–100)"
              type="number"
              value={formData.priority}
              onChange={handleChange("priority")}
            />
          </Grid>

          {/* Start Date */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              value={formData.startDate}
              onChange={handleChange("startDate")}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* End Date */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="End Date"
              value={formData.endDate}
              onChange={handleChange("endDate")}
              InputLabelProps={{ shrink: true }}
              error={!!errors.endDate}
              helperText={errors.endDate}
            />
          </Grid>

          {/* Visible Plans */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Visible To Plans</InputLabel>
              <Select
                multiple
                value={formData.visiblePlans}
                onChange={handleChange("visiblePlans")}
                label="Visible To Plans"
              >
                {Object.values(USER_ACCOUNT_TYPES).map((plan) => (
                  <MenuItem key={plan.value} value={plan.value}>
                    {plan.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Status */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={formData.status}
                onChange={handleChange("status")}
              >
                {Object.values(AD_STATUS).map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? "Updating..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditAdModal;
